import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import i18n from "../../i18n/config";
import { authSelector } from "../Auth/AuthSlice";
import { hasRole } from "../../lib/utility";

const WRAPPER_ID = "widget-iframe-wrapper";
const IFRAME_ID = "widget-iframe";
const EDGE = 14;
const LEGACY_POSITION_KEY = "chatWidgetPosition";

const removeWidget = () => {
  const existingWrapper = document.getElementById(WRAPPER_ID);
  if (existingWrapper) {
    existingWrapper.remove();
    return;
  }
  const existingIframe = document.getElementById(IFRAME_ID);
  if (existingIframe) {
    existingIframe.remove();
  }
};

const ChatWidget = () => {
  const { user } = useSelector(authSelector);
  const stripsRef = useRef([]);
  const iframeRef = useRef(null);

  const isAuthenticated = Boolean(
    (hasRole(user) || user?.accessToken) && globalThis.isAuthenticated
  );

  useEffect(() => {
    const isWidgetEnabled =
      import.meta.env.VITE_ENABLE_CHAT_WIDGET === "true" ||
      import.meta.env.VITE_ENABLE_CHAT_WIDGET === true;

    if (!isWidgetEnabled || !isAuthenticated) {
      removeWidget();
      return;
    }

    if (document.getElementById(WRAPPER_ID) || document.getElementById(IFRAME_ID)) {
      return;
    }

    const WIDGET_ORIGIN =
      import.meta.env.VITE_CHAT_WIDGET_ORIGIN || "https://aiqawidget.broadnet.ai";
    const CHANNEL_TOKEN = import.meta.env.VITE_CHAT_WIDGET_CHANNEL_TOKEN;

    if (!CHANNEL_TOKEN) {
      console.error(
        "Chat widget channel token is missing. Set VITE_CHAT_WIDGET_CHANNEL_TOKEN."
      );
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.id = WRAPPER_ID;
    wrapper.style.position = "fixed";
    wrapper.style.zIndex = "1000";
    wrapper.style.width = "64px";
    wrapper.style.height = "64px";
    wrapper.style.borderRadius = "50%";
    wrapper.style.transition =
      "width 0.3s ease, height 0.3s ease, border-radius 0.3s ease";
    wrapper.style.overflow = "hidden";
    wrapper.style.boxSizing = "border-box";

    // Session-only position — always reset to default bottom-right on reload
    let customPosition = null;
    try {
      localStorage.removeItem(LEGACY_POSITION_KEY);
    } catch {
      // ignore storage errors
    }

    const BUBBLE_SIZE = 64;
    const margin = 16;

    const applyDefaultPosition = () => {
      wrapper.style.left = "auto";
      wrapper.style.top = "auto";
      wrapper.style.right = "20px";
      wrapper.style.bottom = "20px";
    };

    const setLeftTop = (left, top) => {
      const maxLeft = Math.max(0, window.innerWidth - wrapper.offsetWidth);
      const maxTop = Math.max(0, window.innerHeight - wrapper.offsetHeight);
      const clampedLeft = Math.min(Math.max(0, left), maxLeft);
      const clampedTop = Math.min(Math.max(0, top), maxTop);

      wrapper.style.right = "auto";
      wrapper.style.bottom = "auto";
      wrapper.style.left = `${clampedLeft}px`;
      wrapper.style.top = `${clampedTop}px`;

      return { left: clampedLeft, top: clampedTop };
    };

    const moveBubble = (left, top) => {
      customPosition = setLeftTop(left, top);
    };

    // Place the expanded panel so it opens upward when the bubble is near the bottom
    const applyExpandedPosition = (width, height) => {
      if (!customPosition) {
        wrapper.style.left = "auto";
        wrapper.style.top = "auto";
        wrapper.style.right = `${margin}px`;
        wrapper.style.bottom = `${margin}px`;
        return;
      }

      const bubbleLeft = customPosition.left;
      const bubbleTop = customPosition.top;
      const bubbleBottom = bubbleTop + BUBBLE_SIZE;
      const bubbleRight = bubbleLeft + BUBBLE_SIZE;

      const openUpward = bubbleTop + BUBBLE_SIZE / 2 > window.innerHeight / 2;
      const openLeftward = bubbleLeft + BUBBLE_SIZE / 2 > window.innerWidth / 2;

      let left = openLeftward ? bubbleRight - width : bubbleLeft;
      let top = openUpward ? bubbleBottom - height : bubbleTop;

      left = Math.min(
        Math.max(margin, left),
        window.innerWidth - margin - width
      );
      top = Math.min(
        Math.max(margin, top),
        window.innerHeight - margin - height
      );

      wrapper.style.right = "auto";
      wrapper.style.bottom = "auto";
      wrapper.style.left = `${left}px`;
      wrapper.style.top = `${top}px`;
    };

    if (customPosition) {
      setLeftTop(customPosition.left, customPosition.top);
    } else {
      applyDefaultPosition();
    }

    const iframe = document.createElement("iframe");
    iframe.id = IFRAME_ID;
    iframe.src = `${WIDGET_ORIGIN}/?channelToken=${CHANNEL_TOKEN}`;
    iframe.width = "100%";
    iframe.height = "100%";
    iframe.frameBorder = "0";
    iframe.allow = "microphone *; autoplay *; clipboard-write *";
    iframe.title = i18n.t("chatWidget.title", { defaultValue: "Chat Widget" });
    iframeRef.current = iframe;
    iframe.style.background = "transparent";
    iframe.style.border = "none";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.display = "block";
    iframe.style.borderRadius = "inherit";
    iframe.style.overflow = "hidden";
    iframe.style.pointerEvents = "auto";

    // Invisible edge strips for dragging — center stays free so clicks open chat
    const dragLayer = document.createElement("div");
    dragLayer.style.position = "absolute";
    dragLayer.style.inset = "0";
    dragLayer.style.zIndex = "2";
    dragLayer.style.pointerEvents = "none";

    const stripStyles = [
      `top:0;left:0;right:0;height:${EDGE}px`,
      `bottom:0;left:0;right:0;height:${EDGE}px`,
      `top:${EDGE}px;bottom:${EDGE}px;left:0;width:${EDGE}px`,
      `top:${EDGE}px;bottom:${EDGE}px;right:0;width:${EDGE}px`,
    ];

    const strips = stripStyles.map((pos) => {
      const strip = document.createElement("div");
      strip.style.cssText = `position:absolute;${pos};pointer-events:auto;cursor:grab;touch-action:none;user-select:none;background:transparent`;
      strip.title = i18n.t("chatWidget.dragToMove", {
        defaultValue: "Drag to move",
      });
      dragLayer.appendChild(strip);
      return strip;
    });
    stripsRef.current = strips;

    wrapper.appendChild(iframe);
    wrapper.appendChild(dragLayer);
    document.body.appendChild(wrapper);

    let isExpanded = false;
    let preferredWidth = 420;
    let preferredHeight = 480;

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    const setDragLayerVisible = (visible) => {
      dragLayer.style.display = visible ? "block" : "none";
    };

    const applySize = () => {
      if (!isExpanded) {
        wrapper.style.width = `${BUBBLE_SIZE}px`;
        wrapper.style.height = `${BUBBLE_SIZE}px`;
        wrapper.style.borderRadius = "50%";
        wrapper.style.overflow = "hidden";
        setDragLayerVisible(true);

        if (customPosition) {
          setLeftTop(customPosition.left, customPosition.top);
        } else {
          applyDefaultPosition();
        }
        return;
      }

      const width = Math.min(
        preferredWidth || 420,
        window.innerWidth - margin * 2
      );
      const height = Math.min(
        preferredHeight || 480,
        window.innerHeight - margin * 2
      );

      // Position first while still at bubble size so open-upward math is correct,
      // then grow the panel.
      applyExpandedPosition(width, height);
      wrapper.style.width = `${width}px`;
      wrapper.style.height = `${height}px`;
      wrapper.style.borderRadius = "12px";
      wrapper.style.overflow = "hidden";
      setDragLayerVisible(false);
    };

    const onPointerMove = (event) => {
      if (!dragging) return;
      event.preventDefault();
      wrapper.style.transition = "none";
      moveBubble(
        startLeft + (event.clientX - startX),
        startTop + (event.clientY - startY)
      );
    };

    const onPointerUp = (event) => {
      if (!dragging) return;

      dragging = false;
      strips.forEach((strip) => {
        strip.style.cursor = "grab";
      });
      iframe.style.pointerEvents = "auto";
      wrapper.style.transition =
        "width 0.3s ease, height 0.3s ease, border-radius 0.3s ease";

      try {
        event.currentTarget?.releasePointerCapture?.(event.pointerId);
      } catch {
        // ignore
      }

      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };

    const onPointerDown = (event) => {
      if (isExpanded || event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      dragging = true;
      strips.forEach((strip) => {
        strip.style.cursor = "grabbing";
      });
      iframe.style.pointerEvents = "none";

      const rect = wrapper.getBoundingClientRect();
      startX = event.clientX;
      startY = event.clientY;
      startLeft = rect.left;
      startTop = rect.top;

      wrapper.style.right = "auto";
      wrapper.style.bottom = "auto";
      wrapper.style.left = `${startLeft}px`;
      wrapper.style.top = `${startTop}px`;

      event.currentTarget.setPointerCapture(event.pointerId);
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      window.addEventListener("pointercancel", onPointerUp);
    };

    strips.forEach((strip) => {
      strip.addEventListener("pointerdown", onPointerDown);
    });

    const handleMessage = (event) => {
      if (event.origin !== WIDGET_ORIGIN) return;
      if (!event.data || typeof event.data !== "object") return;

      if (event.data.type === "widgetResize") {
        isExpanded = !!event.data.isExpanded;
        preferredWidth = event.data.width || 420;
        preferredHeight = event.data.height || 480;
        applySize();
      }

      if (event.data.type === "requestAuthToken") {
        try {
          const authKey = event.data.auth_key;
          const authPath = event.data.auth_path;
          if (!authKey) return;

          const raw = localStorage.getItem(authKey);
          if (!raw) return;

          let token = null;
          if (!authPath) {
            token = raw;
          } else {
            let obj;
            try {
              obj = JSON.parse(raw);
            } catch {
              obj = null;
            }
            if (obj) {
              const path = authPath.split(".");
              for (let i = 0; i < path.length; i++) {
                if (obj === null || obj === undefined) break;
                if (typeof obj === "string") {
                  try {
                    obj = JSON.parse(obj);
                  } catch {
                    obj = null;
                    break;
                  }
                }
                obj = obj[path[i]];
              }
              token = typeof obj === "string" ? obj : null;
            }
          }

          if (token && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              { type: "authToken", token },
              WIDGET_ORIGIN
            );
          }
        } catch (e) {
          console.error("Failed to send auth token to widget", e);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("resize", applySize);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      strips.forEach((strip) => {
        strip.removeEventListener("pointerdown", onPointerDown);
      });
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("resize", applySize);
      stripsRef.current = [];
      iframeRef.current = null;
      removeWidget();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleLanguageChange = () => {
      const dragTooltip = i18n.t("chatWidget.dragToMove", {
        defaultValue: "Drag to move",
      });
      stripsRef.current.forEach((strip) => {
        strip.title = dragTooltip;
      });
      if (iframeRef.current) {
        iframeRef.current.title = i18n.t("chatWidget.title", {
          defaultValue: "Chat Widget",
        });
      }
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  return null;
};

export default ChatWidget;
